import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Image from 'components/global/Image';
import NoRecordsFound from 'components/global/NoRecordsFound';
import PageHeader from 'components/global/PageHeader';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import DynamicListPagination from 'components/list/DynamicListPagination';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';

import { OTO_STORE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faCartShopping } from '@fortawesome/pro-light-svg-icons';
import { faBagsShopping } from '@fortawesome/pro-regular-svg-icons';
import { faCartShoppingFast as faCartShoppingFastSolid, faXmark } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OTOStore.styled';
import OTOStoreCartModal from './OTOStoreCartModal';
import { getUserCart, setCurrentPage, setSearchText, updateUserCart } from './OTOStoreSlice';

const OTOStore = () => {
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showError } = useAlert();
	const navigate = useNavigate();
	const userCart = useSelector((state) => state.otoStore.cart);
	const { searchText, currentPage } = useSelector((state) => state.otoStore);
	const { translateProductName } = useTranslateProductName();

	const [isLoading, setIsLoading] = useState(true);
	const [pageSize, setPageSize] = useState(10);
	const [productData, setProductData] = useState({
		products: [],
		offset: 0,
		size: 10
	});
	const debouncedSearchText = useDebounce(searchText, 1000);

	const getProducts = async () => {
		setIsLoading(true);

		try {
			const response = await axios.post(OTO_STORE.GET_PRODUCTS, {
				paging: {
					offset: (currentPage - 1) * pageSize,
					count: pageSize
				},
				...(debouncedSearchText && { searchText: debouncedSearchText })
			});
			if (response.status === 200) {
				setProductData({
					products: response.data?.data,
					offset: response.data?.offset,
					size: response.data?.size
				});
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getProducts();
	}, [pageSize, currentPage, debouncedSearchText]);

	const handleAddToCart = async (e, productId) => {
		try {
			e.stopPropagation();
			e.preventDefault();
			const product = userCart?.items?.find((item) => item.productId === productId);

			await dispatch(
				updateUserCart({
					productId,
					quantity: product?.quantity ? product?.quantity + 1 : 1
				})
			).unwrap();
		} catch (error) {
			showError();
			AppLogger.error(error);
		}
	};

	const findAttribute = useCallback(
		(product, key) => product?.productAttributes?.find?.((item) => item?.attributeName === key)?.attributeValue,
		[]
	);

	return (
		<div>
			<PageHeader
				title="otoStore"
				tooltip="otoStoreTooltip"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/oto-store.json.gz">
				<S.PageHeaderActionContainer>
					<div>
						<StringInput
							value={searchText}
							setValue={(value) => dispatch(setSearchText(value))}
							placeholder="search"
						/>
					</div>
					<Button
						label="myOrders"
						variant="outline"
						icon={faBagsShopping}
						onClick={() => navigate(PATHS.OTO_STORE_MY_ORDERS)}
					/>
					<HeaderCartButton />
				</S.PageHeaderActionContainer>
			</PageHeader>
			{searchText && debouncedSearchText && !isLoading && (
				<S.SearchInfoContainer>
					<Typography variant="p2" color="var(--content-3)">
						<Trans
							i18nKey="otoStoreSearchInfo"
							values={{
								searchText: debouncedSearchText,
								count: productData.size
							}}
						/>
					</Typography>
					<Button
						variant="borderless"
						iconButton
						size="small"
						iconSize="small"
						icon={faXmark}
						onClick={() => dispatch(setSearchText(null))}
					/>
				</S.SearchInfoContainer>
			)}
			<S.PageContainer>
				<S.ProductsContainer>
					{isLoading
						? [...Array(pageSize)].map((_, i) => (
								<S.LoadingRow key={i}>
									<Skeleton width="100%" height="28rem" />
									<S.ProductContent>
										<Skeleton width="30%" height="2rem" />
										<Skeleton width="65%" height="2rem" />
										<Skeleton width="45%" height="2rem" />
									</S.ProductContent>
								</S.LoadingRow>
							))
						: productData.products.length > 0 &&
							productData?.products?.map?.((product) => (
								<SingleProduct
									key={`product_${product.ID}`}
									productId={product.ID}
									name={translateProductName(product)}
									attribute={findAttribute(product, 'size')}
									currency={product.currency}
									price={product.sellingPrice || product?.price}
									image={product?.productImage}
									handleAddToCart={handleAddToCart}
									isCartLoading={userCart?.isLoading}
									goToDetails={() =>
										navigate(
											`${PATHS.OTO_STORE_PRODUCT}?productId=${product.ID}&name=${translateProductName(product)}`
										)
									}
								/>
							))}
				</S.ProductsContainer>
				{!isLoading && productData.products.length === 0 && (
					<NoRecordsFound
						style={{ margin: '0 auto' }}
						title="otoStoreNoProducts"
						subTitle="otoStoreNoProductsDescription"
					/>
				)}
			</S.PageContainer>
			<div>
				<DynamicListPagination
					currentPage={currentPage}
					totalCount={productData.size}
					pageSize={pageSize}
					onPageChange={(page) => dispatch(setCurrentPage(page))}
					setPageSize={setPageSize}
					containerStyle={{
						background: 'transparent',
						padding: 0
					}}
				/>
			</div>
		</div>
	);
};

const SingleProduct = ({
	productId,
	name,
	attribute,
	currency,
	price,
	image,
	handleAddToCart,
	goToDetails,
	isCartLoading
}) => (
	<S.ProductContainer onClick={() => goToDetails()}>
		<S.AddToCartButton
			className="add-cart"
			onClick={(e) => handleAddToCart(e, productId)}
			icon={faCartShoppingFastSolid}
			iconButton
			loading={isCartLoading}
		/>
		<Image
			className="product-image"
			url={image}
			width="28rem"
			height="28rem"
			style={{
				borderRadius: '1rem'
			}}
		/>
		<S.ProductContent>
			<Typography variant="p2" withCurrency currency={currency}>
				{price}
			</Typography>
			<Typography variant="p2" fontWeight="500">
				{name}
			</Typography>
			<Typography color="var(--content-3)">{attribute}</Typography>
		</S.ProductContent>
	</S.ProductContainer>
);

export const HeaderCartButton = () => {
	const dispatch = useDispatch();
	const userCart = useSelector((state) => state.otoStore.cart);

	const cartCount = useMemo(() => {
		const cart = userCart?.items?.length;
		if (cart > 100) {
			return '99+';
		} else {
			return cart;
		}
	}, [userCart?.items]);

	const handleOpenMyCart = () => {
		NiceModal.show(OTOStoreCartModal);
	};

	useEffect(() => {
		dispatch(getUserCart());
	}, []);

	return (
		<Button
			label="myCart"
			variant="blue"
			icon={faCartShopping}
			onClick={handleOpenMyCart}
			loading={userCart?.isLoading}>
			<S.CartBadge>
				<Typography color="var(--blue)" fontSize="1.2rem" fontWeight="500">
					{cartCount}
				</Typography>
			</S.CartBadge>
		</Button>
	);
};

export const useTranslateProductName = () => {
	const { LanguageCode } = useLocale();

	const findAttribute = useCallback(
		(product, key) => product?.productAttributes?.find?.((item) => item?.attributeName === key)?.attributeValue,
		[]
	);

	const translateProductName = useCallback(
		(product) => {
			let name;
			switch (LanguageCode) {
				case 'arabicValue':
					name = findAttribute(product, 'productNameAR');
					break;
				case 'engValue':
					name = findAttribute(product, 'productNameEN');
					break;
				default:
					name = findAttribute(product, 'productNameEN');
					break;
			}
			return name || product?.name;
		},
		[LanguageCode]
	);

	return { translateProductName };
};

export default OTOStore;
